import { CloseOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { Badge } from 'antd';
import { type CSSProperties, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Popover } from 'components/ui';
import {
  type PictureInferenceFilterOptionGroup,
  hasDeltaTIncompatibleDetectionType,
} from 'horizon/routes/Inspections/InspectionDetail/pictureInferenceFilter.utils';
import { useDeltaTSlider } from 'horizon/routes/Inspections/InspectionDetail/useDeltaTSlider';

import { StyledButton } from '../ImageViewer.style';

import {
  Content,
  Group,
  GroupLabel,
  OptionLabel,
  OptionText,
  ThresholdControl,
  ThresholdHeader,
} from './InferenceFilterTool.style';

const InferenceFilterButton = styled(StyledButton)`
  .ant-badge,
  .anticon,
  svg {
    color: inherit;
  }
`;

type InferenceFilterToolProps = {
  disabled?: boolean;
  onChange: (data: {
    selectedInferenceDetectionTypeIds?: string[];
    selectedInferenceDeltaTMin?: number;
  }) => void;
  options: PictureInferenceFilterOptionGroup[];
  selectedInferenceDeltaTMin?: number;
  value?: string[];
};

export const InferenceFilterTool = ({
  disabled = false,
  onChange,
  options,
  selectedInferenceDeltaTMin,
  value = [],
}: InferenceFilterToolProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const selectedIds = useMemo(() => new Set(value), [value]);
  const deltaTSlider = useDeltaTSlider({
    value: selectedInferenceDeltaTMin,
    onChange: nextValue => onChange({ selectedInferenceDeltaTMin: nextValue }),
  });

  const handleOptionChange = (optionValue: string) => {
    const nextSelectedIds = selectedIds.has(optionValue)
      ? value.filter(id => id !== optionValue)
      : [...value, optionValue];
    const isIncompatible = hasDeltaTIncompatibleDetectionType(nextSelectedIds);

    if (isIncompatible) {
      deltaTSlider.clearDraft();
    }
    onChange({
      selectedInferenceDetectionTypeIds: nextSelectedIds,
      ...(isIncompatible ? { selectedInferenceDeltaTMin: undefined } : {}),
    });
  };

  const content = (
    <Content>
      {options.map(group => (
        <Group key={group.inferenceApplication}>
          <GroupLabel>{group.inferenceApplication}</GroupLabel>
          {group.options.map(option => (
            <OptionLabel key={option.value} title={option.description}>
              <input
                type="checkbox"
                checked={selectedIds.has(option.value)}
                onChange={() => handleOptionChange(option.value)}
              />
              <OptionText>{option.label}</OptionText>
            </OptionLabel>
          ))}
          {group.deltaTRange &&
            (() => {
              const min = group.deltaTRange.min ?? 0;
              const max = group.deltaTRange.max ?? 50;
              const sliderValue = deltaTSlider.draftValue ?? min;
              const isDeltaTDisabled = hasDeltaTIncompatibleDetectionType(value);

              return (
                <>
                  <ThresholdHeader title={group.deltaTRange.description ?? ''}>
                    <span>{group.deltaTRange.label}</span>
                    {selectedInferenceDeltaTMin !== undefined && (
                      <button
                        type="button"
                        aria-label={t('images.clearThreshold', 'Clear {{label}}', {
                          label: group.deltaTRange.label,
                        })}
                        onClick={deltaTSlider.clear}
                      >
                        <CloseOutlined />
                      </button>
                    )}
                  </ThresholdHeader>
                  <ThresholdControl
                    $disabled={isDeltaTDisabled}
                    style={
                      {
                        '--threshold-position': `${deltaTSlider.getPosition(
                          sliderValue,
                          min,
                          max
                        )}%`,
                      } as CSSProperties
                    }
                  >
                    <input
                      type="range"
                      min={min}
                      max={max}
                      value={sliderValue}
                      disabled={disabled || isDeltaTDisabled}
                      aria-label={t('images.thresholdMinimum', '{{label}} minimum', {
                        label: group.deltaTRange.label,
                      })}
                      onChange={event => {
                        const nextValue = Number(event.currentTarget.value);
                        deltaTSlider.handleChange(nextValue);
                      }}
                    />
                    <div className="threshold-value">
                      {isDeltaTDisabled
                        ? t('inspection.notApplicable', 'Not applicable')
                        : deltaTSlider.draftValue === undefined
                          ? t('inspection.any', 'Any')
                          : deltaTSlider.formatValue(deltaTSlider.draftValue)}
                    </div>
                  </ThresholdControl>
                </>
              );
            })()}
        </Group>
      ))}
    </Content>
  );

  return (
    <Popover
      content={content}
      open={open}
      placement="leftTop"
      trigger="click"
      onOpenChange={setOpen}
      arrow={false}
    >
      <InferenceFilterButton
        data-testid="image-controls-inference-filter"
        title={t('inspection.aiFilters', 'AI filters')}
        type={value.length > 0 || selectedInferenceDeltaTMin !== undefined ? 'primary' : 'default'}
        disabled={disabled}
      >
        <Badge
          count={value.length + Number(selectedInferenceDeltaTMin !== undefined)}
          size="small"
          offset={[8, -4]}
        >
          <ThunderboltOutlined />
        </Badge>
      </InferenceFilterButton>
    </Popover>
  );
};
